import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

import { useError } from '@/composables/useError'
import {
  FlowRepository,
  type IOperationPropertiesResponse,
} from '@/services/api/flow'
import type { IFlow, INode } from '@/types/flow/FlowNodeTypes'

const defaultZone = {
  isActive: false,
  zone: '',
}

interface SheetType {
  isOpen: boolean
  stepId: null | string
}

export const useFlowStore = defineStore('flowStore', () => {
  const flow = ref<IFlow | null>(null)
  const isFlowPublishLoading = ref<boolean>(false)
  const flowID = computed(() => flow.value?.uuid)
  const integrationProperties = ref<IOperationPropertiesResponse[]>([])
  const { t } = useI18n()
  const { handleError } = useError()

  const step = ref<SheetType>({
    isOpen: false,
    stepId: null,
  })

  const isEditable = computed<boolean>(() => flow.value?.status === 'draft')

  const openStep = (stepID: string): void => {
    step.value = {
      isOpen: true,
      stepId: stepID,
    }
  }

  const closeSheet = (): void => {
    step.value = {
      isOpen: false,
      stepId: null,
    }
  }

  const zoneChange = ref(defaultZone)
  const cleanupZone = (): void => {
    zoneChange.value = { ...defaultZone }
  }

  const fetchFlow = async (flowUuid?: string): Promise<void> => {
    const typeSafeUuid = flowUuid || flowID.value
    if (!typeSafeUuid) return
    try {
      const { data } = await FlowRepository.getFlowById(typeSafeUuid)
      flow.value = data
    } catch (e) {
      throw e
    }
  }

  const onPublish = async (): Promise<boolean> => {
    if (!flow.value?.uuid || isFlowPublishLoading.value) return false
    try {
      isFlowPublishLoading.value = true

      await FlowRepository.publishDraft(flow.value.uuid)
      toast.success(t('flow.publishSuccess'))
      const { data } = await FlowRepository.getFlowById(flow.value.uuid)
      flow.value = data
      return true
    } catch (e) {
      handleError(e)
      return false
    } finally {
      isFlowPublishLoading.value = false
    }
  }

  const getIntegrationProperties = async (): Promise<void> => {
    try {
      const result = await FlowRepository.getIntegrationProperties()
      integrationProperties.value = result.data
    } catch (e) {
      throw e
    }
  }

  const removeStep = (step: INode): void => {
    if (!flow.value) return

    flow.value.options = flow.value.options.filter(el => {
      return !step.options?.some(opt => opt.uuid === el.uuid)
    })

    flow.value.steps = flow.value.steps.filter(el => el.uuid != step.uuid)
  }

  return {
    flow,
    isFlowPublishLoading,
    flowID,
    fetchFlow,
    zoneChange,
    cleanupZone,
    step,
    removeStep,
    openStep,
    closeSheet,
    isEditable,
    onPublish,
    integrationProperties,
    getIntegrationProperties,
  }
})
